import { useState, useCallback } from 'react';
import { User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { reauthenticateWithCredential, EmailAuthProvider, updatePassword } from 'firebase/auth';

interface ProfileData {
    name: string;
    email: string;
}

interface PasswordData {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

interface UseProfileUpdateReturn {
    profileData: ProfileData;
    passwordData: PasswordData;
    profileLoading: boolean;
    passwordLoading: boolean;
    profileError: string | null;
    passwordError: string | null;
    profileSuccess: string | null;
    passwordSuccess: string | null;
    setProfileData: (data: ProfileData) => void;
    setPasswordData: (data: PasswordData) => void;
    updateProfile: (name: string, user: User | null) => Promise<void>;
    changePassword: (currentPassword: string, newPassword: string, confirmPassword: string, user: User | null) => Promise<void>;
    clearProfileError: () => void;
    clearPasswordError: () => void;
    clearProfileSuccess: () => void;
    clearPasswordSuccess: () => void;
}

export const useProfileUpdate = (initialProfileData: ProfileData): UseProfileUpdateReturn => {
    const [profileData, setProfileData] = useState<ProfileData>(initialProfileData);
    const [passwordData, setPasswordData] = useState<PasswordData>({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [profileLoading, setProfileLoading] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [profileError, setProfileError] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
    const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

    const updateOwnProfileFn = httpsCallable(functions, 'updateOwnProfile');

    // Update profile
    const updateProfile = useCallback(async (name: string, user: User | null) => {
        setProfileLoading(true);
        setProfileError(null);
        setProfileSuccess(null);

        try {
            await updateOwnProfileFn({ name });
            setProfileSuccess('Profil uppdaterad framgångsrikt!');

            // Update the user's display name in the auth state
            if (user) {
                await user.reload();
            }
        } catch (err: any) {
            setProfileError(err.message || 'Ett fel uppstod vid uppdatering av profil');
            throw err;
        } finally {
            setProfileLoading(false);
        }
    }, [updateOwnProfileFn]);

    // Change password
    const changePassword = useCallback(async (
        currentPassword: string,
        newPassword: string,
        confirmPassword: string,
        user: User | null
    ) => {
        setPasswordLoading(true);
        setPasswordError(null);
        setPasswordSuccess(null);

        // Client-side validation
        if (newPassword !== confirmPassword) {
            setPasswordError('Nya lösenordet matchar inte bekräftelsen');
            setPasswordLoading(false);
            throw new Error('Passwords do not match');
        }

        if (newPassword.length < 6) {
            setPasswordError('Nya lösenordet måste vara minst 6 tecken långt');
            setPasswordLoading(false);
            throw new Error('Password too short');
        }

        if (!user || !user.email) {
            setPasswordError('Användarinformation saknas');
            setPasswordLoading(false);
            throw new Error('User information missing');
        }

        try {
            // Re-authenticate user with current password
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Update password using Firebase Auth
            await updatePassword(user, newPassword);

            setPasswordSuccess('Lösenord ändrat framgångsrikt!');
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (err: any) {
            console.error('Password change error:', err);
            if (err.code === 'auth/wrong-password') {
                setPasswordError('Nuvarande lösenord är felaktigt');
            } else if (err.code === 'auth/weak-password') {
                setPasswordError('Nya lösenordet är för svagt');
            } else {
                setPasswordError(err.message || 'Ett fel uppstod vid ändring av lösenord');
            }
            throw err;
        } finally {
            setPasswordLoading(false);
        }
    }, []);

    // Clear functions
    const clearProfileError = useCallback(() => setProfileError(null), []);
    const clearPasswordError = useCallback(() => setPasswordError(null), []);
    const clearProfileSuccess = useCallback(() => setProfileSuccess(null), []);
    const clearPasswordSuccess = useCallback(() => setPasswordSuccess(null), []);

    return {
        profileData,
        passwordData,
        profileLoading,
        passwordLoading,
        profileError,
        passwordError,
        profileSuccess,
        passwordSuccess,
        setProfileData,
        setPasswordData,
        updateProfile,
        changePassword,
        clearProfileError,
        clearPasswordError,
        clearProfileSuccess,
        clearPasswordSuccess,
    };
};
